import {
  ApiResponse,
  AdSupplierGroup,
  PaginationQuery,
  PaginationResponse,
} from '@magnetar/admin-api-interfaces'
import { useAuth } from './useAuth'
import { useGet } from './useFetch'

interface UseAdSupplierGroupsParams extends PaginationQuery {
  is_active?: boolean
  provider_id?: number
}

export function useAdSupplierGroups({
  page = 1,
  pageSize = 10,
  is_active,
  provider_id,
}: UseAdSupplierGroupsParams) {
  const { token } = useAuth()

  const queryParams = new URLSearchParams()
  queryParams.append('page', String(page))
  queryParams.append('pageSize', String(pageSize))
  if (is_active !== undefined) queryParams.append('is_active', String(is_active))
  if (provider_id !== undefined) queryParams.append('provider_id', String(provider_id))

  const { data, error, mutate } = useGet<ApiResponse<PaginationResponse<AdSupplierGroup>>>(
    token ? `/api/ad-supplier-groups?${queryParams.toString()}` : null,
    undefined,
    token ?? '',
  )

  return {
    adSupplierGroups: data?.data?.items,
    pagination: data?.data
      ? {
          page: data.data.page,
          pageSize: data.data.pageSize,
          total: data.data.total,
          totalPages: data.data.totalPages,
        }
      : undefined,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

export function useAdSupplierGroup(id: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<ApiResponse<AdSupplierGroup>>(
    token && id ? `/api/ad-supplier-groups/${id}` : null,
    undefined,
    token ?? '',
  )

  return {
    adSupplierGroup: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// Hook to get suppliers by ad supplier group
export function useAdSupplierGroupSuppliers(groupId: number | null) {
  const { token } = useAuth()
  const { data, error, mutate } = useGet<
    ApiResponse<{ supplier_id: number; supplier_name: string; is_active: boolean }[]>
  >(token && groupId ? `/api/ad-supplier-groups/${groupId}/suppliers` : null, undefined, token ?? '')

  return {
    suppliers: data?.data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}

// Hook to get available suppliers for assignment to an ad supplier group
export function useAvailableSuppliersForGroup(
  groupId: number | null,
  params?: { search?: string; page?: number; pageSize?: number },
) {
  const { token } = useAuth()
  const queryParams = new URLSearchParams()
  if (params?.search) queryParams.append('search', params.search)
  if (params?.page) queryParams.append('page', String(params.page))
  if (params?.pageSize) queryParams.append('pageSize', String(params.pageSize))

  const queryString = queryParams.toString()
  const url = queryString
    ? `/api/ad-supplier-groups/${groupId}/available-suppliers?${queryString}`
    : `/api/ad-supplier-groups/${groupId}/available-suppliers`

  const { data, error, mutate } = useGet<
    ApiResponse<{
      adSuppliers: Array<{
        supplier_id: number
        supplier_name: string
        manager: string
        phone: string
      }>
      total: number
      page: number
      pageSize: number
    }>
  >(token && groupId ? url : null, undefined, token ?? '')

  return {
    adSuppliers: data?.data?.adSuppliers,
    total: data?.data?.total,
    page: data?.data?.page,
    pageSize: data?.data?.pageSize,
    isLoading: !error && !data,
    isError: error,
    mutate,
  }
}
